import {
  esperaRetentativa,
  uuidv7,
  type Checklist,
  type CampoChecklist,
  type Obra,
  type ResultadoEnvio,
} from '@qsms/shared'
import { ErroApi, SemRede, api } from './api.js'
import {
  CHAVES,
  banco,
  gravarAjuste,
  lerAjuste,
  removerInspecaoLocal,
  type InspecaoLocal,
  type ItemFila,
} from './banco.js'

/// O motor que leva o que está no aparelho até o servidor, e traz de volta o que
/// mudou lá.
///
/// Roda sozinho: quando o app abre, quando a rede volta, a cada 2 minutos com rede,
/// e quando o usuário pede. Nunca roda duas vezes ao mesmo tempo.
///
/// Ordem importa: **primeiro os arquivos, depois a inspeção**. Uma inspeção que
/// chega antes das fotos é gravada sem elas, e a foto que sobe depois não tem mais
/// onde se prender até o próximo envio.

export type EstadoSincronizacao =
  | { fase: 'parado'; ultimaEm: string | null; pendentes: number }
  | { fase: 'sincronizando'; passo: string }
  | { fase: 'erro'; mensagem: string; pendentes: number }

type Ouvinte = (estado: EstadoSincronizacao) => void

const ouvintes = new Set<Ouvinte>()
let estado: EstadoSincronizacao = { fase: 'parado', ultimaEm: null, pendentes: 0 }
let rodando = false
/// Alguém pediu para enviar enquanto um ciclo já rodava. Sem isto o pedido morria:
/// "Finalizar" no meio do ciclo de 2 minutos esperava o próximo, e quem fechava o
/// app logo depois só via a interdição subir no dia seguinte.
let repetir = false
let temporizador: number | null = null

/// Há um ciclo rodando agora?
export function sincronizando(): boolean {
  return rodando
}

export function observarSincronizacao(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte)
  ouvinte(estado)
  return () => ouvintes.delete(ouvinte)
}

function anunciar(novo: EstadoSincronizacao) {
  estado = novo
  for (const o of ouvintes) o(novo)
}

async function contarPendentes(): Promise<number> {
  return banco.fila.count()
}

// ---------------------------------------------------------------- entrada

/// Garante o item da fila, **sem** mandar enviar.
///
/// É o caminho da digitação, e pode rodar dentro da transação de quem grava a
/// inspeção. Antes, cada pausa de 600 ms numa inspeção já enviada disparava um
/// ciclo inteiro de rede — envio, fotos, cadastros, descida — e cada ciclo
/// regravava a inspeção três ou quatro vezes enquanto a pessoa digitava. O envio
/// fica para "Salvar", "Finalizar", a saída da tela e o ciclo de 2 minutos.
///
/// A mutação ganha id novo sempre: o servidor responde a um id repetido com o
/// resultado guardado, sem aplicar o conteúdo — e o conteúdo mudou.
export async function registrarNaFila(inspecaoId: string): Promise<void> {
  const agora = new Date().toISOString()
  const jaNaFila = await banco.fila.where('inspecaoId').equals(inspecaoId).first()
  if (jaNaFila) {
    // Substitui a mutação pendente: vale o estado mais recente, não o intermediário.
    await banco.fila.update(jaNaFila.id, {
      clientMutationId: uuidv7(),
      tentativas: 0,
      proximaTentativa: agora,
      ultimoErro: null,
    })
  } else {
    await banco.fila.add({
      id: uuidv7(),
      inspecaoId,
      clientMutationId: uuidv7(),
      criadoEm: agora,
      tentativas: 0,
      proximaTentativa: agora,
      ultimoErro: null,
    })
  }
}

/// Põe a inspeção na fila e manda enviar já. Para quando a pessoa conclui algo:
/// criar, salvar, finalizar, sair da tela.
export async function enfileirar(inspecaoId: string): Promise<void> {
  await registrarNaFila(inspecaoId)
  await banco.inspecoes.update(inspecaoId, { sincronizacao: 'PENDENTE', problema: null })
  void sincronizar({ aguardarVez: true })
}

// ---------------------------------------------------------------- ciclo

export async function sincronizar(
  opcoes: { forcado?: boolean; aguardarVez?: boolean } = {},
): Promise<void> {
  if (rodando) {
    // Os gatilhos automáticos (2 min, rede de volta) podem se perder: o ciclo em
    // curso já faz o trabalho deles. Um pedido da pessoa não.
    if (opcoes.aguardarVez || opcoes.forcado) repetir = true
    return
  }
  rodando = true
  try {
    anunciar({ fase: 'sincronizando', passo: 'verificando conexão' })
    if (!navigator.onLine && !opcoes.forcado) {
      anunciar({ fase: 'parado', ultimaEm: await lerAjuste(CHAVES.ultimoPullCadastros, null), pendentes: await contarPendentes() })
      return
    }

    anunciar({ fase: 'sincronizando', passo: 'enviando inspeções' })
    await drenarFila()

    anunciar({ fase: 'sincronizando', passo: 'atualizando cadastros' })
    await puxarCadastros()

    anunciar({ fase: 'sincronizando', passo: 'buscando atualizações' })
    await puxarInspecoes()

    anunciar({
      fase: 'parado',
      ultimaEm: new Date().toISOString(),
      pendentes: await contarPendentes(),
    })
  } catch (e) {
    const mensagem = e instanceof SemRede ? 'Sem conexão. Tentarei de novo.' : (e as Error).message
    anunciar({ fase: 'erro', mensagem, pendentes: await contarPendentes() })
  } finally {
    rodando = false
    if (repetir) {
      repetir = false
      void sincronizar()
    }
  }
}

// ---------------------------------------------------------------- envio

/// O que saiu de cada inspeção, para conferir na volta se o aparelho ainda está no
/// mesmo ponto.
interface Enviado {
  /// O id de mutação que a linha da fila tinha quando o envio foi montado.
  cmid: string
  atualizadoEm: string
  /// Preenchido quando o envio foi a sondagem de uma mutação sem resposta.
  incerta: string | null
}

async function drenarFila(): Promise<void> {
  const agora = new Date().toISOString()
  const prontos = await banco.fila.where('proximaTentativa').belowOrEqual(agora).toArray()
  if (prontos.length === 0) return

  // Em lotes pequenos: uma inspeção com 10 fotos já é uma requisição pesada na rede
  // de uma obra, e lote grande que falha desperdiça tudo.
  for (let i = 0; i < prontos.length; i += 5) {
    const ids = prontos.slice(i, i + 5).map((p) => p.id)

    // Fila e inspeção relidas agora, juntas, e não da lista do começo do ciclo: o
    // lote anterior pode ter levado dezenas de segundos subindo fotos, e a pessoa
    // pode ter mexido nesse meio tempo. Montar o envio com o id de mutação velho e
    // o conteúdo novo fazia o servidor responder "já recebi" sem aplicar nada.
    const pares = await banco.transaction('rw', banco.fila, banco.inspecoes, async () => {
      const lista: Array<{ item: ItemFila; inspecao: InspecaoLocal }> = []
      for (const id of ids) {
        const item = await banco.fila.get(id)
        if (!item) continue
        const inspecao = await banco.inspecoes.get(item.inspecaoId)
        if (!inspecao) {
          // A inspeção sumiu (usuário apagou); a fila não tem mais o que enviar.
          await banco.fila.delete(item.id)
          continue
        }
        lista.push({ item, inspecao })
      }
      if (lista.length > 0) {
        await banco.inspecoes.bulkUpdate(
          lista.map(({ inspecao }) => ({
            key: inspecao.id,
            changes: { sincronizacao: 'ENVIANDO' as const, tentouEnvio: true },
          })),
        )
      }
      return lista
    })
    if (pares.length === 0) continue

    const lote = pares.map((p) => p.item)
    const inspecoes = pares.map((p) => p.inspecao)
    // A foto do que foi enviado. Se a inspeção mudar enquanto o envio viaja — a
    // pessoa continua digitando —, a resposta do servidor não pode marcá-la como
    // enviada: o que está no aparelho já é outra coisa.
    const enviado = new Map<string, Enviado>(
      pares.map(({ item, inspecao }) => [
        inspecao.id,
        { cmid: item.clientMutationId, atualizadoEm: inspecao.atualizadoEm, incerta: item.mutacaoIncerta ?? null },
      ]),
    )

    const envios = pares.map(({ item, inspecao }) => {
        return {
          id: inspecao.id,
          // Um envio anterior ficou sem resposta: repete o id dele. Se o servidor o
          // gravou, responde a versão em que está sem aplicar nada; se não, aplica
          // o conteúdo atual. Os dois casos são seguros — ver aplicarResultado.
          clientMutationId: item.mutacaoIncerta ?? item.clientMutationId,
          codigo: inspecao.codigo,
          checklistId: inspecao.checklistId,
          obraId: inspecao.obraId,
          status: inspecao.status,
          titulo: inspecao.titulo,
          dataCriacao: inspecao.dataCriacao,
          assinaturaUsuario: inspecao.assinaturaUsuario,
          registradoEm: inspecao.registradoEm,
          versaoBase: inspecao.versaoServidor,
          respostas: inspecao.respostas,
          fotos: inspecao.fotos,
        }
      })

    try {
      // O envio dos arquivos entra no mesmo try do envio da inspeção. Fora dele,
      // uma única foto problemática abortava o ciclo inteiro — parava de enviar e
      // de receber, a cada 2 minutos, sem dizer qual inspeção era a culpada.
      await enviarArquivosDe(inspecoes)

      const { resultados } = await api.enviarInspecoes(envios)
      for (const bruto of resultados) {
        await aplicarResultado(bruto as ResultadoEnvio, lote, enviado)
      }
    } catch (e) {
      await adiarLote(lote, e)
      // Sem rede não adianta tentar os lotes seguintes.
      if (e instanceof SemRede) throw e
    }
  }
}

async function aplicarResultado(
  resultado: ResultadoEnvio,
  lote: ItemFila[],
  enviado: Map<string, Enviado>,
) {
  const item = lote.find((l) => l.inspecaoId === resultado.id)
  const env = enviado.get(resultado.id)

  // Conferir e gravar numa transação só. Antes a conferência era numa e a
  // gravação em outra: a gravação da tela cabia no meio, e a edição que ela acabava
  // de pôr na fila era marcada ENVIADO e apagada da fila sem ter subido.
  await banco.transaction('rw', banco.inspecoes, banco.fila, async () => {
    const atual = await banco.inspecoes.get(resultado.id)
    const linha = item ? await banco.fila.get(item.id) : undefined
    if (!atual) {
      if (linha) await banco.fila.delete(linha.id)
      return
    }

    if (resultado.resultado === 'ok') {
      // Três casos em que "ok" não quer dizer "o que está aqui está lá":
      // - foi a sondagem de um envio sem resposta: o servidor pode ter respondido
      //   "já recebi" sem aplicar o conteúdo de agora;
      // - a pessoa mexeu enquanto o envio viajava;
      // - a linha da fila ganhou id novo nesse meio tempo (outra gravação).
      // Nos três, guarda a versão do servidor — para o próximo envio não virar
      // conflito consigo mesmo — e deixa na fila para subir o que falta. No pior
      // caso sobe uma vez a mais; nunca some nada.
      const mudou =
        !env ||
        env.incerta !== null ||
        atual.atualizadoEm !== env.atualizadoEm ||
        (linha !== undefined && linha.clientMutationId !== env.cmid)
      if (mudou) {
        await banco.inspecoes.update(resultado.id, {
          versaoServidor: resultado.versao,
          sincronizacao: 'PENDENTE',
        })
        if (linha) await banco.fila.update(linha.id, { mutacaoIncerta: null })
        await registrarNaFila(resultado.id)
        return
      }

      const faltam = resultado.arquivosPendentes.length
      await banco.inspecoes.update(resultado.id, {
        versaoServidor: resultado.versao,
        sincronizacao: faltam > 0 ? 'PENDENTE' : 'ENVIADO',
        problema: faltam > 0 ? `${faltam} foto(s) ainda subindo.` : null,
      })
      if (!linha) return
      // Só sai da fila quando não falta mais nada.
      if (faltam === 0) {
        await banco.fila.delete(linha.id)
        return
      }
      // Conta as tentativas em vez de zerá-las: uma foto que o servidor recusa em
      // definitivo (tipo inválido, blob já descartado) reagendava de 5 em 5
      // segundos para sempre, incrementando a versão e enchendo a auditoria a cada
      // volta. Depois de algumas tentativas, a inspeção sobe sem ela e o usuário
      // fica sabendo.
      const tentativas = (linha.tentativas ?? 0) + 1
      if (tentativas >= 5) {
        await banco.fila.delete(linha.id)
        await banco.inspecoes.update(resultado.id, {
          sincronizacao: 'ENVIADO',
          problema:
            `A inspeção foi enviada, mas ${faltam} foto(s) não subiram. ` +
            'Abra a inspeção e adicione as fotos de novo.',
        })
      } else {
        await banco.fila.update(linha.id, {
          clientMutationId: uuidv7(),
          tentativas,
          proximaTentativa: new Date(Date.now() + esperaRetentativa(tentativas)).toISOString(),
        })
      }
      return
    }

    if (resultado.resultado === 'conflito') {
      // Não sobrescreve nem descarta: o usuário decide na tela de conflito.
      await banco.inspecoes.update(resultado.id, {
        sincronizacao: 'BLOQUEADO',
        problema:
          'Esta inspeção também foi alterada no servidor. Abra para comparar e escolher qual versão vale.',
      })
      if (linha) await banco.fila.delete(linha.id)
      return
    }

    const motivos = resultado.problemas.map((p) => `${p.campoNome}: ${p.mensagem}`).join(' · ')
    await banco.inspecoes.update(resultado.id, {
      sincronizacao: 'BLOQUEADO',
      problema: motivos || 'O servidor recusou esta inspeção.',
    })
    if (linha) await banco.fila.delete(linha.id)
  })
}

async function adiarLote(lote: ItemFila[], erro: unknown) {
  const mensagem = erro instanceof SemRede ? 'Sem conexão.' : (erro as Error).message
  // 401 significa sessão morta: insistir só gasta bateria.
  const definitivo = erro instanceof ErroApi && erro.status === 401
  // Sem resposta (rede caiu, servidor quebrou no meio), o servidor pode ter
  // gravado. Uma recusa clara (4xx) diz que não gravou.
  const semResposta = !(erro instanceof ErroApi && erro.status < 500)

  for (const item of lote) {
    const tentativas = item.tentativas + 1
    await banco.fila.update(item.id, {
      tentativas,
      ultimoErro: mensagem,
      proximaTentativa: new Date(Date.now() + esperaRetentativa(tentativas)).toISOString(),
      // O id que saiu: o da sondagem anterior, se era uma, ou o desta tentativa.
      // Não é trocado pelas gravações da tela, ao contrário do clientMutationId.
      ...(semResposta ? { mutacaoIncerta: item.mutacaoIncerta ?? item.clientMutationId } : {}),
    })
    // A partir da terceira tentativa o problema deixa de ser passageiro e o usuário
    // precisa saber — antes a mensagem era apagada e o app retentava em silêncio.
    await banco.inspecoes.update(item.inspecaoId, {
      sincronizacao: definitivo ? 'BLOQUEADO' : 'PENDENTE',
      problema: definitivo
        ? 'Sessão expirada. Entre novamente para enviar.'
        : tentativas >= 3
          ? `Não consegui enviar (${tentativas} tentativas): ${mensagem}`
          : null,
    })
  }
}

/// Sobe as fotos que o servidor ainda não tem. Pergunta antes de enviar: numa obra
/// com 4G fraco, subir 8 MB à toa custa caro.
async function enviarArquivosDe(inspecoes: InspecaoLocal[]): Promise<void> {
  const ids = inspecoes.flatMap((i) => i.fotos.map((f) => f.arquivoId))
  const respostasComArquivo = inspecoes.flatMap((i) =>
    i.respostas.map((r) => r.arquivoId).filter((a): a is string => !!a),
  )
  const todos = [...new Set([...ids, ...respostasComArquivo])]
  if (todos.length === 0) return

  const { faltando } = await api.verificarArquivos(todos)
  for (const arquivoId of faltando) {
    const local = await banco.fotos.get(arquivoId)
    if (!local) continue // o blob já foi descartado; nada a fazer
    try {
      await api.enviarArquivo(arquivoId, local.blob, `${arquivoId}.jpg`)
      await banco.fotos.update(arquivoId, { enviado: true })
    } catch (e) {
      if (e instanceof SemRede) throw e
      // Uma foto que o servidor recusa não pode travar a inspeção inteira.
      console.warn(`falha ao enviar foto ${arquivoId}`, e)
    }
  }
}

// ---------------------------------------------------------------- descida

async function puxarCadastros(): Promise<void> {
  const desde = await lerAjuste<string | null>(CHAVES.ultimoPullCadastros, null)
  const pacote = await api.cadastros(desde ?? undefined)

  await banco.transaction('rw', [banco.checklists, banco.campos, banco.obras, banco.pessoas], async () => {
    if (pacote.completo) {
      await Promise.all([banco.checklists.clear(), banco.campos.clear(), banco.obras.clear(), banco.pessoas.clear()])
    }
    const checklists = pacote.checklists as Array<Checklist & { campos: CampoChecklist[] }>
    if (checklists.length > 0) {
      await banco.checklists.bulkPut(checklists.map(({ campos: _campos, ...c }) => c as Checklist))
      const campos = checklists.flatMap((c) => c.campos)
      // Substitui os campos do checklist inteiro: campo removido lá precisa sumir aqui.
      for (const c of checklists) {
        await banco.campos.where('checklistId').equals(c.id).delete()
      }
      if (campos.length > 0) await banco.campos.bulkPut(campos)
    }
    if (pacote.obras.length > 0) await banco.obras.bulkPut(pacote.obras as Obra[])
    if (pacote.pessoas.length > 0) await banco.pessoas.bulkPut(pacote.pessoas)

    if (pacote.removidos.obras.length > 0) await banco.obras.bulkDelete(pacote.removidos.obras)
    for (const id of pacote.removidos.checklists) {
      await banco.checklists.delete(id)
      await banco.campos.where('checklistId').equals(id).delete()
    }
  })

  if (pacote.listas) await gravarAjuste(CHAVES.listasDePessoas, pacote.listas)
  if (pacote.responsaveisNc) await gravarAjuste(CHAVES.responsaveisNc, pacote.responsaveisNc)
  await gravarAjuste(CHAVES.ultimoPullCadastros, pacote.ate)
}

/// Traz do servidor o que mudou nas obras do usuário — em especial a Criticidade
/// Ápia definida pelo SESMT. Não mexe no que ainda está para subir.
/// Até 23/09/2026, a exclusão feita no servidor não chegava aos aparelhos (ver
/// `aplicarInspecoesBaixadas`). Uma vez por aparelho, a descida volta até antes da
/// primeira exclusão — traz de novo só o que mudou desde então, duas inspeções — e
/// as cópias presas saem.
const RECONCILIAR_EXCLUSOES_DESDE = '2026-09-23T18:40:00.000Z'

async function puxarInspecoes(): Promise<void> {
  let desde = await lerAjuste<string | null>(CHAVES.ultimoPullInspecoes, null)
  const reconciliado = await lerAjuste<boolean>(CHAVES.exclusoesReconciliadas, false)
  if (!reconciliado && desde && desde > RECONCILIAR_EXCLUSOES_DESDE) {
    desde = RECONCILIAR_EXCLUSOES_DESDE
  }

  // O servidor devolve no máximo 500 por vez e avisa quando há mais. Sem este laço,
  // um aparelho novo recebia as 500 inspeções mais antigas da obra e nunca via o
  // resto — e como o carimbo avançava, nunca mais pediria.
  for (let pagina = 0; pagina < 50; pagina++) {
    const { ate, inspecoes, temMais } = await api.baixarInspecoes(desde ?? undefined)
    await aplicarInspecoesBaixadas(inspecoes as Array<Record<string, any>>)
    desde = ate
    await gravarAjuste(CHAVES.ultimoPullInspecoes, ate)
    if (!reconciliado) await gravarAjuste(CHAVES.exclusoesReconciliadas, true)
    if (!temMais || inspecoes.length === 0) return
  }
}

async function aplicarInspecoesBaixadas(inspecoes: Array<Record<string, any>>): Promise<void> {
  for (const bruta of inspecoes) {
    // Ler e gravar na mesma transação: entre um e outro, a tela de preenchimento
    // pode ter gravado o que a pessoa digitou.
    await banco.transaction('rw', banco.inspecoes, banco.fila, banco.fotos, () =>
      aplicarUmaBaixada(bruta),
    )
  }
}

async function aplicarUmaBaixada(bruta: Record<string, any>): Promise<void> {
  const local = await banco.inspecoes.get(bruta.id as string)

  // A Criticidade Ápia é do servidor — o aparelho nunca a envia —, então desce
  // sempre, mesmo quando o resto da inspeção fica como está aqui. Sem mexer em
  // atualizadoEm: é por ele que o envio confere se a pessoa mexeu.
  const criticidade = bruta.criticidadeApia ?? null
  if (local && !bruta.excluidoEm && criticidade !== (local.criticidadeApia ?? null)) {
    await banco.inspecoes.update(local.id, { criticidadeApia: criticidade })
  }

  // Nunca sobrescreve trabalho que ainda não subiu.
  if (local && local.sincronizacao !== 'ENVIADO' && !local.remota) return

  // Excluída no servidor — por este aparelho ou por outro. Antes, ela era
  // regravada aqui como se existisse: sumia no clique de excluir e voltava na
  // sincronização seguinte, e a próxima tentativa dava "não encontrada".
  if (bruta.excluidoEm) {
    if (local) await removerInspecaoLocal(local.id)
    return
  }

  // O eco do próprio envio: o servidor devolve o que acabou de receber daqui.
  // Regravar só serviria para redesenhar a tela de quem está preenchendo. Vale só
  // para as feitas neste aparelho: as remotas descem sempre, porque a carga do
  // Power Apps as corrige sem aumentar a versão.
  if (
    local &&
    !local.remota &&
    local.versaoServidor != null &&
    Number(bruta.versao) <= local.versaoServidor
  ) {
    return
  }

  await banco.inspecoes.put({
    id: bruta.id,
    codigo: bruta.codigo,
    checklistId: bruta.checklistId,
    obraId: bruta.obraId,
    status: bruta.status,
    titulo: bruta.titulo ?? null,
    dataCriacao: bruta.dataCriacao ? String(bruta.dataCriacao).slice(0, 10) : null,
    assinaturaUsuario: bruta.assinaturaUsuario ?? null,
    criticidadeApia: bruta.criticidadeApia ?? null,
    registradoEm: bruta.registradoEm,
    atualizadoEm: bruta.atualizadoEm ?? bruta.registradoEm,
    respostas: (bruta.respostas ?? []).map(mapearResposta),
    fotos: (bruta.fotos ?? []).map((f: Record<string, any>) => ({
      id: f.id,
      arquivoId: f.arquivoId,
      descricao: f.descricao ?? null,
      ordem: f.ordem ?? 0,
    })),
    versaoServidor: bruta.versao,
    sincronizacao: 'ENVIADO',
    problema: null,
    // A origem não muda com a descida. Antes era `!local`: na segunda descida, a
    // inspeção de outra pessoa virava "feita aqui" e abria para edição.
    remota: local ? local.remota : true,
  })
}

function mapearResposta(r: Record<string, any>) {
  return {
    id: r.id,
    campoChecklistId: r.campoChecklistId,
    campoNome: r.campoNome,
    campoTipo: r.campoTipo,
    ordem: r.ordem ?? 0,
    texto: r.valorTexto ?? null,
    textoLongo: r.valorTextoLongo ?? null,
    numero: r.valorNumero != null ? Number(r.valorNumero) : null,
    data: r.valorData ? String(r.valorData).slice(0, 10) : null,
    booleano: r.valorBooleano ?? null,
    opcoes: r.valorOpcoes ?? [],
    arquivoId: r.arquivoId ?? null,
    ncResponsavel: r.ncResponsavel ?? null,
    ncResponsavelId: r.ncResponsavelId ?? null,
    ncAcao: r.ncAcao ?? null,
    ncPrazo: r.ncPrazo ? String(r.ncPrazo).slice(0, 10) : null,
  }
}

// ---------------------------------------------------------------- agenda

/// Liga o ciclo automático. Chamado uma vez, no arranque do app.
export function iniciarSincronizacaoAutomatica(): () => void {
  const aoVoltarRede = () => void sincronizar()
  const aoVoltarAoApp = () => {
    if (document.visibilityState === 'visible') void sincronizar()
  }

  window.addEventListener('online', aoVoltarRede)
  document.addEventListener('visibilitychange', aoVoltarAoApp)
  // 2 minutos: frequente o bastante para o SESMT ver a inspeção logo, raro o
  // bastante para não drenar a bateria de quem passa o dia em campo.
  temporizador = window.setInterval(() => void sincronizar(), 120_000)

  void sincronizar()

  return () => {
    window.removeEventListener('online', aoVoltarRede)
    document.removeEventListener('visibilitychange', aoVoltarAoApp)
    if (temporizador !== null) window.clearInterval(temporizador)
  }
}
